import {
  CheckboxController,
  Collapse,
  RichController,
  SwitchController,
  Text,
  TextAreaController,
} from "@pushpress/shared-components";
import { Button } from "@pushpress/pushpress-ui";
import { type Control, useWatch, useFormContext } from "react-hook-form";
import { PlanForm } from "../PlanDrawer.types";
import { useContext, useState, useRef } from "react";
import { useGeneratePlanText } from "../../../../apis/aiApi.hooks";
import { TextType } from "../../../../apis/aiApi";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../../../../utils/misc";
import { Icons } from "../../../../icons";
import { cn } from "../../../../utils/css";
import { AppContext } from "../../../../context/app";
import { RichInputInstance } from "@pushpress/shared-components/build/components/inputs/RichInput/RichInput.types";

interface SalesSectionProps {
  control: Control<PlanForm>;
}

const LoadingOverlay = () => (
  <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white/30">
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
      <div className="tw-relative tw-w-8 tw-h-8">
        <div className="tw-absolute tw-top-0 tw-left-0 tw-w-8 tw-h-8 tw-border-2 tw-border-gray-200 tw-rounded-full"></div>
        <div
          className="tw-absolute tw-top-0 tw-left-0 tw-w-8 tw-h-8 tw-border-2 tw-border-blue-500 tw-rounded-full tw-animate-spin"
          style={{
            borderTopColor: "transparent",
            animationDuration: "1s",
            animationTimingFunction: "linear",
            animationIterationCount: "infinite",
          }}
        />
      </div>
      <span className="tw-text-sm tw-text-black">Generating text...</span>
    </div>
  </div>
);

const SalesSection = (props: SalesSectionProps) => {
  const { client } = useContext(AppContext);
  const { control } = props;
  const { setValue, getValues } = useFormContext<PlanForm>();
  const generatePlanText = useGeneratePlanText();
  const [generatingStates, setGeneratingStates] = useState<
    Record<TextType, boolean>
  >({
    confirmationMessage: false,
    shortDescription: false,
    longDescription: false,
  });
  const canGenerateMessages = client?.bankConnected === 1;

  const [publishToPublic, classAccess] = useWatch({
    control,
    name: ["publishToPublic", "classAccess"],
  });

  const richInputRefs = useRef<Record<TextType, RichInputInstance | null>>({
    confirmationMessage: null,
    shortDescription: null,
    longDescription: null,
  });

  const handleGenerate = async (textType: TextType) => {
    const planData = getValues();

    if (!planData) {
      toast.error(
        "Cannot generate text: Missing plan data",
        defaultToastOptions,
      );
      return;
    }

    try {
      setGeneratingStates((prev) => ({ ...prev, [textType]: true }));
      const { completion } = await generatePlanText.mutateAsync({
        textType,
        plan: planData,
      });

      if (
        textType === "confirmationMessage" ||
        textType === "longDescription"
      ) {
        richInputRefs.current[textType]?.setHtmlValue(completion);
      }

      setValue(textType, completion, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      setGeneratingStates((prev) => ({ ...prev, [textType]: false }));
    } catch (error) {
      toast.error(
        "Failed to generate text. Please try again.",
        defaultToastOptions,
      );
      setGeneratingStates((prev) => ({ ...prev, [textType]: false }));
    }
  };

  const GenerateButton = ({
    textType,
    label,
  }: {
    textType: TextType;
    label: string;
  }) => (
    <Button
      variant="outline"
      size="sm"
      onClick={() => handleGenerate(textType)}
      disabled={generatingStates[textType]}
      className="tw-mt-2"
    >
      {generatingStates[textType] ? (
        <span>Generating...</span>
      ) : (
        <div className="tw-flex tw-items-center tw-gap-1">
          <Icons.Sparkles className="tw-w-3 tw-h-3 tw-text-yellow-400" />{" "}
          {label}
        </div>
      )}
    </Button>
  );

  return (
    <div>
      <Text component="h2" variant="heading-5" weight="600" color="wolf-800">
        Online sales
      </Text>
      <Text variant="body-md" color="wolf-600" className="tw-mt-1">
        Should this plan be sold externally?
      </Text>
      <SwitchController
        control={control}
        name="publishToPublic"
        label="Publish sales page"
        className="tw-mt-4"
      />
      <Text variant="body-md" color="wolf-600" className="tw-mt-1">
        Sell this plan via an external landing page you can share with clients.
      </Text>
      <Collapse in={publishToPublic}>
        <CheckboxController
          control={control}
          name="sellOnMemberApp"
          label="Members app"
          className="tw-mt-4"
        />
        <CheckboxController
          control={control}
          name="sellOnStaffApp"
          label="Staff app (kiosk mode)"
          className="tw-mt-4"
        />
        <CheckboxController
          control={control}
          name="sellOnWebsitePlanList"
          label="Website plan list"
          className="tw-mt-4"
        />
        <div className="tw-relative">
          <div className="tw-relative">
            <RichController
              control={control}
              name="confirmationMessage"
              label="Confirmation message"
              height="200px"
              multiLineToolbarPosition="bottom"
              helperText="Add a custom message that shows on the purchase confirmation page."
              className={cn(
                "tw-mt-4",
                generatingStates.confirmationMessage && "tw-opacity-10",
              )}
              valueType="html"
              disabled={generatingStates.confirmationMessage}
              ref={(ref) => (richInputRefs.current.confirmationMessage = ref)}
              labelButton={
                canGenerateMessages && (
                  <div className="tw-pb-1">
                    <GenerateButton
                      textType="confirmationMessage"
                      label="Generate"
                    />
                  </div>
                )
              }
            />
            {generatingStates.confirmationMessage && <LoadingOverlay />}
          </div>
        </div>
        {classAccess.value !== "none" && (
          <>
            <CheckboxController
              control={control}
              name="allowClassBooking"
              label="Allow class booking"
              className="tw-mt-4"
            />
            <Text variant="body-md" color="wolf-600" className="tw-mt-1">
              After purchase, should clients be prompted to reserve a class?
            </Text>
          </>
        )}
        <div className="tw-relative">
          <div className="tw-relative">
            <TextAreaController
              control={control}
              name="shortDescription"
              label="Short description"
              className={cn(
                "tw-mt-4",
                generatingStates.shortDescription && "tw-opacity-10",
              )}
              disabled={generatingStates.shortDescription}
              labelButton={
                canGenerateMessages && (
                  <div className="tw-pb-1">
                    <GenerateButton
                      textType="shortDescription"
                      label="Generate"
                    />
                  </div>
                )
              }
            />
            {generatingStates.shortDescription && <LoadingOverlay />}
          </div>
        </div>
        <div className="tw-relative">
          <div className="tw-relative">
            <RichController
              control={control}
              name="longDescription"
              label="Long description"
              height="200px"
              multiLineToolbarPosition="bottom"
              valueType="html"
              className={cn(
                generatingStates.longDescription && "tw-opacity-10",
              )}
              disabled={generatingStates.longDescription}
              ref={(ref) => (richInputRefs.current.longDescription = ref)}
              labelButton={
                canGenerateMessages && (
                  <div className="tw-pb-1">
                    <GenerateButton
                      textType="longDescription"
                      label="Generate"
                    />
                  </div>
                )
              }
            />
            {generatingStates.longDescription && <LoadingOverlay />}
          </div>
        </div>
      </Collapse>
    </div>
  );
};

export default SalesSection;
